import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import BranchSelect from "../../../components/ui/BranchSelect";
import {
  useCorrectionList,
  useReviewCorrection,
} from "../../../api/queries/workflowQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/apiError";
import {
  formatDateTime,
  formatPlainDate,
  formatTime,
  fromNow,
} from "../../../utils/dateTime";
import ApprovalTrail, { LevelBadge } from "./ApprovalTrail";
import { useCanModify } from "../../../hooks/usePermissions";
import { approverGroupText } from "../../../utils/waitingFor";

const EMPTY = [];
const STATUSES = [
  { value: "PENDING", label: "Pending", tone: "amber" },
  { value: "APPROVED", label: "Approved", tone: "green" },
  { value: "REJECTED", label: "Rejected", tone: "red" },
  { value: "CANCELLED", label: "Cancelled", tone: "gray" },
];
const TONE = Object.fromEntries(STATUSES.map((s) => [s.value, s.tone]));

/** "08:00 – 17:00" from { checkInTime, checkOutTime }; null = no record. */
const Times = ({ t, changedFrom }) => {
  if (!t) return <span className="text-slate-400">No record</span>;
  const part = (key) => {
    const v = t[key];
    const changed = changedFrom !== undefined && v;
    return (
      <span className={changed ? "fw-semibold text-primary" : ""}>
        {v ? formatTime(v) : changedFrom !== undefined ? "same" : "—"}
      </span>
    );
  };
  return (
    <span className="text-nowrap">
      {part("checkInTime")} – {part("checkOutTime")}
    </span>
  );
};

const ReviewDialog = ({ correction: c, action, onClose }) => {
  const isReject = action === "REJECT";
  const [remarks, setRemarks] = useState("");
  const review = useReviewCorrection();
  const levels = c.levels;
  const isLast = true; // one step: the first decision is final

  const submit = () =>
    review.mutate(
      { id: c.id, action, remarks: remarks.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(
            isReject
              ? "Correction rejected."
              : isLast
                ? "Correction approved: the day is updated."
                : `Level ${levels.current} approved; sent to the next level.`,
          );
          onClose();
        },
        onError: (err) => {
          // 403 is already shown by the global handler
          if (getApiErrorStatus(err) !== 403)
            toast.error(getApiErrorMessage(err, "Could not save the review."));
        },
      },
    );

  return (
    <Dialog
      title={isReject ? "Reject correction" : "Approve correction"}
      subtitle={`${c.employee?.name || "Employee"} · ${formatPlainDate(c.date, "ddd, MMM D")}`}
      onClose={onClose}
      busy={review.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={review.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`btn profile-btn ${isReject ? "btn-danger" : "btn-success"}`}
            onClick={submit}
            disabled={review.isPending || (isReject && !remarks.trim())}
          >
            {review.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isReject ? "Reject" : "Approve"}
          </button>
        </>
      }
    >
      <div className="eval-summary">
        <div className="fs-085">
          <strong>Was:</strong> <Times t={c.original} />
        </div>
        <div className="fs-085">
          <strong>Asked for:</strong>{" "}
          <Times t={c.requested} changedFrom={c.original} />
        </div>
        {c.reason && (
          <div className="fs-080 text-slate-600 mt-1">“{c.reason}”</div>
        )}
      </div>
      {(c.approvals || EMPTY).length > 0 && (
        <div className="mb-3">
          <ApprovalTrail approvals={c.approvals} levels={levels} />
        </div>
      )}
      {!isReject && isLast && (
        <p className="fs-080 text-slate-600">
          Approving changes the attendance day and recomputes its hours.
        </p>
      )}
      <label htmlFor="corr-remarks" className="profile-label">
        {isReject ? (
          <>
            Reason <span className="text-danger">*</span>
          </>
        ) : (
          "Remarks (optional)"
        )}
      </label>
      <textarea
        id="corr-remarks"
        className="form-control"
        rows={2}
        maxLength={500}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        autoFocus={isReject}
      />
    </Dialog>
  );
};

/** Attendance corrections filed by internal employees, reviewed by their DTR approvers. */
const CorrectionQueue = () => {
  const [status, setStatus] = useState("PENDING");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [branchId, setBranchId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [reviewing, setReviewing] = useState(null);
  const canModify = useCanModify("dtr"); // + the server's canReview (my turn)

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const query = useCorrectionList({ status, branchId, search, page, limit });
  const rows = query.data?.data || EMPTY;

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div
          className="approval-subtabs"
          role="tablist"
          aria-label="Correction status"
        >
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={status === s.value}
              className={`approval-subtab ${status === s.value ? "active" : ""}`}
              onClick={() => {
                setStatus(s.value);
                setPage(1);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className="fs-075 text-slate-500">
          <i className="bi bi-info-circle me-1"></i>
          Internal employees fix their own days here; HR fixes deployed
          employees&apos; days in Attendance.
        </span>
      </div>

      <div className="approval-filters mb-3">
        <div className="position-relative approval-search">
          <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
          <input
            type="search"
            className="filter-input w-100 ps-5"
            placeholder="Search name or employee number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search employees"
          />
        </div>
        <div className="att-filter-branch">
          <BranchSelect
            value={branchId}
            onChange={(v) => {
              setBranchId(v || "");
              setPage(1);
            }}
            id="corr-branch"
          />
        </div>
      </div>

      <div className="glass-table-card">
        {query.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(query.error, "Could not load corrections.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0 approval-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Employee</th>
                    <th className="py-3">Day</th>
                    <th className="py-3">Was</th>
                    <th className="py-3">Asked for</th>
                    <th className="py-3">Reason</th>
                    <th className="py-3">Status</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {rows.map((c) => (
                    <tr key={c.id}>
                      <td className="ps-4">
                        <div className="emp-name-text">
                          {c.employee?.name || "—"}
                        </div>
                        <div className="emp-sub-text">
                          {c.employee?.employeeNumber}
                          {c.employee?.branchName
                            ? ` · ${c.employee.branchName}`
                            : ""}
                        </div>
                      </td>
                      <td className="text-nowrap">
                        {formatPlainDate(c.date, "ddd, MMM D")}
                      </td>
                      <td>
                        <Times t={c.original} />
                      </td>
                      <td>
                        <Times t={c.requested} changedFrom={c.original} />
                      </td>
                      <td>
                        <div className="approval-reason" title={c.reason}>
                          {c.reason || "—"}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`status-pill status-pill--${TONE[c.status] || "gray"}`}
                        >
                          {c.status}
                        </span>
                        {c.status === "PENDING" && (
                          <span className="ms-1">
                            <LevelBadge levels={c.levels} status={c.status} />
                          </span>
                        )}
                        {c.reviewRemarks && (
                          <div className="fs-070 text-slate-500 fst-italic">
                            “{c.reviewRemarks}”
                          </div>
                        )}
                        <div
                          className="fs-070 text-slate-400"
                          title={formatDateTime(c.reviewedAt || c.createdAt)}
                        >
                          {fromNow(c.reviewedAt || c.createdAt)}
                        </div>
                      </td>
                      <td className="pe-4 text-end text-nowrap">
                        {c.status === "PENDING" &&
                          c.canReview === false &&
                          (c.waitingFor?.length ? (
                            <span className="fs-070 text-slate-400">
                              Waiting for{" "}
                              {approverGroupText(c.waitingFor, c.managersAbove)}
                            </span>
                          ) : (
                            c.levels?.total > 1 && (
                              <span className="fs-070 text-slate-400">
                                Another approver is deciding
                              </span>
                            )
                          ))}
                        {c.status === "PENDING" &&
                          c.canReview !== false &&
                          canModify && (
                            <div className="d-inline-flex gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger att-action-btn"
                                onClick={() =>
                                  setReviewing({ c, action: "REJECT" })
                                }
                              >
                                Reject
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-success att-action-btn"
                                onClick={() =>
                                  setReviewing({ c, action: "APPROVE" })
                                }
                              >
                                Approve
                              </button>
                            </div>
                          )}
                      </td>
                    </tr>
                  ))}
                  {!query.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="approval-empty">
                        <i className="bi bi-pencil-square"></i>
                        {status === "PENDING"
                          ? "No corrections waiting for you."
                          : "No corrections."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {query.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={limit}
              total={query.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {reviewing && (
        <ReviewDialog
          correction={reviewing.c}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
};

export default CorrectionQueue;
